export const guidesData = [
  {
    id: "guide-1",
    title: "1. A Guide to Choosing a Southern California Wedding Venue",
    description: "Learn what to look for when comparing Southern California wedding venues, from guest experience and setting to catering, coordination, and total investment.",
    content: {
      paragraphs: [
        "Selecting the perfect Southern California wedding venue sets the foundation for your entire celebration. The region offers a diverse array of aesthetics, but for couples seeking a blend of romance, history, and natural beauty, foothill venues provide an unmatched atmosphere that your guests will talk about for years to come.",
        "When touring spaces, it is essential to look beyond the architecture and feel the atmosphere. Consider the guest experience: Do they feel a sense of wonder upon arrival? Does the venue flow naturally from a sun-drenched ceremony to an enchanting cocktail hour under the olive trees?",
        "At Padua Weddings, we believe transparency and comprehensive support are just as important as our breathtaking Spanish Revival setting. We encourage couples to ask detailed questions about flow and vendor policies, ensuring you feel complete confidence as you begin your journey toward 'I do'."
      ],
      keyTakeaway: "The best Southern California wedding venues pair a breathtaking setting with a seamless, supportive planning experience that prioritizes the guest journey.",
      bullets: [
        "Evaluate the natural, effortless flow of the venue spaces.",
        "Clarify exactly what is included to ensure a stress-free experience.",
        "Consider the accessibility and comfort for traveling guests.",
        "Look for venues with dedicated, warm coordination support."
      ]
    }
  },
  {
    id: "guide-2",
    title: "2. Why Couples Choose Claremont for a Wedding Venue",
    description: "Explore why the Claremont foothills offer a romantic, private, and accessible setting for couples planning a Southern California wedding.",
    content: {
      paragraphs: [
        "Nestled at the base of the San Gabriel Mountains, Claremont offers a rare combination of natural beauty and historic soul. Its leafy streets and vibrant downtown create a welcoming, sophisticated environment for your loved ones to gather and celebrate.",
        "Couples are drawn to Claremont because it offers an escape from the city's frantic pace without sacrificing accessibility. The foothill elevation provides a soft, golden light during sunset that is a photographer's dream, paired with gentle breezes that keep outdoor celebrations comfortable and refreshing.",
        "Padua Weddings embraces this unique Claremont character, offering a secluded, estate-like feel. It’s a place where time seems to slow down, allowing you to be fully present in every romantic moment of your wedding day."
      ],
      keyTakeaway: "Claremont provides a private, scenic, and highly accessible foothill setting that feels like a hidden romantic escape.",
      bullets: [
        "Conveniently located with easy access for Los Angeles and Inland Empire guests.",
        "Offers a private, secluded atmosphere away from the noise of typical city life.",
        "Charming local hotels provide a beautiful 'wedding weekend' experience.",
        "Breathtaking natural lighting and foothill scenery for timeless photography."
      ]
    }
  },
  {
    id: "guide-3",
    title: "3. What Makes a Spanish Revival Wedding Venue Feel Timeless",
    description: "Discover how arches, warm textures, historic architecture, terraces, and old-world details create a romantic wedding atmosphere.",
    content: {
      paragraphs: [
        "Spanish Revival architecture is a hallmark of classic California romance. Defined by smooth stucco walls, terracotta tile roofs, and elegant wrought iron, this style evokes a sense of history and permanence that perfectly mirrors the commitment of marriage.",
        "For weddings, a Spanish Revival setting provides a stunning, textured backdrop that feels both grand and intimate. The warm neutral tones of the architecture complement almost any floral palette, while our terraces offer an enchanting space for dinner under the stars, surrounded by the glow of historic lanterns.",
        "At Padua Weddings, our architectural details—from the grand archways to the intimate courtyards—create a timeless quality. It’s an aesthetic that feels as relevant today as it will in fifty years, ensuring your wedding memories are anchored in a setting of enduring beauty."
      ],
      keyTakeaway: "The inherent warmth and historic soul of a Spanish Revival venue create a naturally romantic atmosphere that transcends trends.",
      bullets: [
        "Architectural archways frame breathtaking ceremony and portrait moments.",
        "Warm terracotta tones create an inviting, Mediterranean-inspired glow.",
        "Courtyards provide intimate spaces for guests to connect and celebrate.",
        "Historic detailing adds a layer of sophistication and depth to your event."
      ]
    }
  },
  {
    id: "guide-4",
    title: "4. How Much Should You Budget for a Southern California Wedding Venue?",
    description: "Understand how venue minimums, guest count, food and beverage, date selection, and enhancements shape the overall wedding investment.",
    content: {
      paragraphs: [
        "Understanding your wedding investment requires looking at the total experience. Many premium venues operate with event minimums to ensure every detail—from the service to the cuisine—meets an elevated standard of excellence.",
        "Your investment is influenced by your vision, your guest count, and the 'feeling' you want to create. While Saturdays in peak season represent a premium investment, Fridays and Sundays offer the same romantic atmosphere with more flexible starting points.",
        "When budgeting, consider the value of your own time and peace of mind. A comprehensive venue like Padua Weddings provides not just a space, but a team of experts dedicated to a seamless execution, often proving more valuable than a 'blank canvas' where every fork and napkin must be sourced separately."
      ],
      keyTakeaway: "Focus on the total value of the experience—including service and peace of mind—to build an accurate and rewarding wedding budget.",
      bullets: [
        "Guest count is the primary driver of your culinary and service investment.",
        "Off-peak days offer the same romantic setting with more flexible minimums.",
        "Comprehensive venues reduce hidden costs and logistical stress.",
        "Always factor in service excellence as a key part of your investment."
      ]
    }
  },
  {
    id: "guide-5",
    title: "5. What Is Included in an All-Inclusive Wedding Venue?",
    description: "Learn how in-house catering, coordination, DJ services, planning guidance, and trusted vendors can simplify the wedding planning process.",
    content: {
      paragraphs: [
        "The best gift you can give yourself during engagement is a simplified planning process. A comprehensive venue model is designed to remove the logistical 'noise,' allowing you to focus on the joy of your celebration and the personalization of your design.",
        "At Padua Weddings, we secure the essential pillars of your day: breathtaking spaces, refined in-house catering, professional coordination, and expert DJ services. This ensures that every member of the team is working in perfect harmony, having executed hundreds of flawless events together.",
        "This integrated approach leads to a significantly more relaxed experience for the couple and a more polished experience for the guests. When the kitchen, the coordinator, and the DJ speak the same language, the transitions feel invisible and the energy remains high."
      ],
      keyTakeaway: "Integrated venue services create a harmonious environment where logistics feel effortless and the focus remains on your celebration.",
      bullets: [
        "Removes the burden of managing dozens of disparate vendor contracts.",
        "Ensures the team executing your day has a deep, intimate knowledge of the space.",
        "Provides a cohesive experience from the first toast to the final dance.",
        "Allows you to be a guest at your own wedding, fully immersed in the moment."
      ]
    }
  },
  {
    id: "guide-6",
    title: "6. Book a Private Tour at Padua Weddings",
    description: "See what to expect during a private tour, how long it takes, what couples can ask, and how starting estimates can be discussed during the visit.",
    content: {
      paragraphs: [
        "A private tour is when your wedding vision truly begins to take shape. It is the moment you step onto the grounds and feel the history and romance of the space for yourself. We invite you to experience the 'Padua Glow' in person.",
        "During your one-hour visit, we walk the path your guests will take—from the grand arrival to the sunset views on the terrace. We discuss your unique story, your preferred season, and the specific atmosphere you want to create for your loved ones.",
        "We believe in transparency. A tour is the perfect time to have a direct, helpful conversation about your investment. We provide guidance on starting estimates tailored to your needs, so you can leave with total clarity and the excitement of knowing you've found 'the one'."
      ],
      keyTakeaway: "A private tour is a personalized, immersive experience designed to help you feel the atmosphere and gain total clarity on your wedding plans.",
      bullets: [
        "Visualize your ceremony and reception in our historic spaces.",
        "Walk the guest journey and feel the flow of the venue.",
        "Receive a personalized investment guide based on your specific needs.",
        "Get immediate, expert answers to your most important planning questions."
      ]
    }
  },
  {
    id: "guide-7",
    title: "7. Real Wedding Investment Examples at Padua Weddings",
    description: "Review sample wedding scenarios that help couples understand how guest count, date, and selected services can affect the starting investment.",
    content: {
      paragraphs: [
        "Every wedding at Padua is a unique reflection of the couple. By looking at real scenarios, you can better understand how to align your guest list and date with your budget goals without compromising on the quality of the experience.",
        "An intimate Sunday gathering might allow for more lavish floral installations or a premium bar experience, while a grand Saturday celebration focuses on the energy of a large group in a historic setting. We help you navigate these choices with ease.",
        "Our team is here to help you maximize the impact of your investment. Whether that means a signature cocktail hour or a late-night snack surprise for your guests, we provide the guidance needed to make every dollar contribute to an unforgettable atmosphere."
      ],
      keyTakeaway: "Aligning your guest count and date with your priorities allows you to create a high-impact, premium experience at any investment level.",
      bullets: [
        "Saturdays offer the most sought-after dates for grand celebrations.",
        "Sundays and weekdays provide a romantic setting with more flexibility.",
        "Smaller guest counts allow for more personalized 'wow' moments.",
        "Clear, transparent estimates help you plan with total confidence."
      ]
    }
  },
  {
    id: "guide-8",
    title: "8. How a Wedding Day Flows at Padua Weddings",
    description: "Walk through the wedding day experience from guest arrival and ceremony to cocktail hour, dinner, dancing, and the final send-off.",
    content: {
      paragraphs: [
        "A great wedding is like a well-composed piece of music—it has a beginning, a middle, and a crescendo. Our venue layout is intentionally designed to guide your guests through a series of beautiful 'reveals' that keep the energy moving and the excitement building.",
        "From the moment of arrival, guests are greeted by the historic charm of our architecture. After a romantic ceremony, the air fills with music and laughter during cocktail hour on the terrace. As the sun sets, the transition into the reception area feels like a natural invitation to the heart of the celebration.",
        "Because our coordination and catering teams work as one, the timing of dinner and toasts is precise and elegant. This leaves more time for what matters most: dancing, connection, and the unforgettable feeling of being surrounded by everyone you love."
      ],
      keyTakeaway: "An intuitive venue flow creates a sense of ease and excitement, allowing your wedding day to unfold like a beautiful story.",
      bullets: [
        "Intentional spaces for every chapter of your wedding day story.",
        "Seamless transitions that keep your guests engaged and comfortable.",
        "Expert timing ensures the energy of the night never dips.",
        "A final send-off that provides a perfect, romantic conclusion to the day."
      ]
    }
  },
  {
    id: "guide-9",
    title: "9. Trusted Wedding Vendors Familiar with Padua Weddings",
    description: "Learn why working with vendors who understand the venue layout, lighting, timing, and flow can help create a smoother wedding day.",
    content: {
      paragraphs: [
        "The most successful weddings are the result of a cohesive creative team. When your vendors have a deep relationship with your venue, they don't just 'show up'—they arrive with a plan and an intimate understanding of how to make the space shine.",
        "A photographer who knows the exact minute the light hits the fountain, or a florist who knows the best way to secure an installation to our historic arches, brings a level of expertise that elevates the final result. They work in tandem with our in-house team like a well-oiled machine.",
        "We provide a curated list of professionals who share our commitment to excellence. These are vendors who love Padua as much as we do, ensuring that every creative element of your wedding is executed with the highest level of care and local knowledge."
      ],
      keyTakeaway: "Collaborating with vendors who know the venue's nuances ensures a higher level of creative execution and a stress-free day.",
      bullets: [
        "Photographers who master the unique 'golden hour' of the foothills.",
        "Florists and designers who understand the scale of our historic spaces.",
        "Vendors who collaborate effortlessly with our in-house coordination.",
        "Peace of mind knowing your team has a proven track record at the venue."
      ]
    }
  },
  {
    id: "guide-10",
    title: "10. Padua Weddings Frequently Asked Questions",
    description: "Find answers to common questions about pricing, tours, catering, coordination, ceremony spaces, reception flow, and planning support.",
    content: {
      paragraphs: [
        "We believe that the most romantic experience is one where you don't have to worry about the details. Providing clear, honest answers to your questions is the first step in building a relationship based on trust and excitement.",
        "Whether you're curious about the specifics of our Spanish Revival architecture or the details of our culinary offerings, we want you to have all the information you need to make the best decision for your future.",
        "Explore our most common inquiries below. If you don't find exactly what you're looking for, we invite you to bring your questions to your private tour, where our team can provide personalized guidance for your unique vision."
      ],
      keyTakeaway: "Absolute clarity and transparency are the foundations of a stress-free and joyful wedding planning journey.",
      bullets: [
        "Immediate answers to your logistical and financial questions.",
        "Understand the benefits of our comprehensive, service-first model.",
        "Clarify how we support your specific vision and theme.",
        "Prepare for a meaningful and productive private venue tour."
      ]
    }
  }
];

export const faqsData = [
  {
    q: "Is Padua Weddings a good venue for a romantic outdoor ceremony?",
    a: "Yes. Padua Weddings features dedicated outdoor spaces framed by historic Spanish Revival architecture and the scenic Claremont foothills, creating a romantic setting for ceremonies that feel intimate, timeless, and refined."
  },
  {
    q: "Does Padua Weddings include catering?",
    a: "Yes. We offer exceptional in-house catering. Our culinary team crafts refined menus from cocktail hour through the final course, ensuring a high-quality, seamless dining experience for all your guests."
  },
  {
    q: "Does Padua Weddings include coordination support?",
    a: "Yes. Professional coordination is a cornerstone of our service. Our team guides your planning journey and manages every detail on the day of, so you can focus entirely on your celebration."
  },
  {
    q: "Can we host both our ceremony and reception at Padua Weddings?",
    a: "Absolutely. The venue is designed for a seamless transition from a beautiful ceremony to an elegant reception, allowing your guests to enjoy the full experience without ever leaving the grounds."
  },
  {
    q: "Does Padua Weddings offer DJ services?",
    a: "Yes. Expert DJ services are included to ensure flawless sound and energy. Our DJs are intimately familiar with the venue's acoustics and flow, providing a professional and energetic entertainment experience."
  },
  {
    q: "What makes Padua Weddings different from other Southern California wedding venues?",
    a: "It is the rare combination of authentic Spanish Revival history, a private foothill setting, and a comprehensive service model. We offer the sophistication of a luxury estate with the warmth of a family-run venue."
  },
  {
    q: "Is Padua Weddings a good alternative to a luxury resort wedding venue?",
    a: "Yes. We provide the same high-level service and all-inclusive convenience of a resort, but with far more privacy, historic soul, and exclusive use of the property for your special day."
  },
  {
    q: "How much does a wedding at Padua Weddings usually cost?",
    a: "Event minimums typically begin around $16,000 for weekdays, $20,000 for Sundays, $23,000 for Fridays, and $31,000 for Saturdays. Your final investment scales with your guest count and chosen enhancements."
  },
  {
    q: "Can we tour Padua Weddings before booking?",
    a: "We highly recommend it. A private tour is the best way to feel the atmosphere and visualize your day. We offer personalized tours by appointment to fit your schedule."
  },
  {
    q: "How long does a private tour take?",
    a: "A private tour typically takes one hour. This allows us to walk the grounds together, discuss your vision, and provide a clear, personalized starting estimate for your wedding."
  },
  {
    q: "Can we receive an estimate during the tour?",
    a: "Yes. We pride ourselves on transparency. Based on your anticipated guest count and date, we can provide clear investment guidance during or immediately following your visit."
  },
  {
    q: "Does Padua Weddings work well for larger weddings?",
    a: "Yes, our versatile spaces elegantly accommodate various guest counts. During your tour, we'll discuss layout options that ensure your celebration feels both grand and comfortable."
  },
  {
    q: "What wedding style fits Padua Weddings best?",
    a: "Our venue naturally enhances romantic, timeless, and old-world aesthetics. The historic architecture and lush greenery provide a stunning foundation for any elegant design vision."
  },
  {
    q: "Does Padua Weddings have preferred vendors?",
    a: "Yes. While we handle the core services internally, we provide a curated list of exceptional photographers, florists, and specialty vendors who have a proven track record of excellence at our venue."
  },
  {
    q: "Is Padua Weddings good for Spanish Revival, romantic, or old-world wedding themes?",
    a: "It is the quintessential setting for these themes. The authentic architecture, archways, and terraces provide a romantic, historic soul that serves as the perfect backdrop for a timeless celebration."
  }
];